import type { ControlPoint, Coordinate, Drawing, PathSegment } from '@/types'
import { isEndPoint, isStartPoint, getOrderedPointIds } from './DrawingPoint.utils'

/**
 * Determine the priority of a lineEnd type.
 * arrow > tShape > none
 */
function lineEndPriority(lineEnd: 'none' | 'arrow' | 'tShape'): number {
	switch (lineEnd) {
		case 'arrow':
			return 2
		case 'tShape':
			return 1
		case 'none':
			return 0
	}
}

/**
 * Select the higher priority lineEnd between two options.
 */
function selectLineEnd(
	a: 'none' | 'arrow' | 'tShape',
	b: 'none' | 'arrow' | 'tShape',
): 'none' | 'arrow' | 'tShape' {
	return lineEndPriority(a) >= lineEndPriority(b) ? a : b
}

/**
 * Compute the merged lineEnd by preserving arrows from junction points.
 */
function computeMergedLineEnd(
	sourceDrawing: Drawing,
	targetDrawing: Drawing,
	sourceIsEnd: boolean,
	targetIsStart: boolean,
	targetIsEnd: boolean,
): 'none' | 'arrow' | 'tShape' {
	let mergedLineEnd: 'none' | 'arrow' | 'tShape' = 'none'

	// Source's junction was source's END - its arrow is consumed, transfer it
	if (sourceIsEnd) {
		mergedLineEnd = selectLineEnd(mergedLineEnd, sourceDrawing.style.lineEnd)
	}
	// Target's junction was target's END - its arrow is consumed, transfer it
	if (targetIsEnd) {
		mergedLineEnd = selectLineEnd(mergedLineEnd, targetDrawing.style.lineEnd)
	}
	// Target's terminal END becomes merged END (if junction was START)
	if (targetIsStart) {
		mergedLineEnd = selectLineEnd(mergedLineEnd, targetDrawing.style.lineEnd)
	}

	return mergedLineEnd
}

/**
 * Remap target point IDs to avoid collisions with source points.
 * Junction point maps to source point ID, others get new IDs.
 */
function remapTargetPointIds(
	sourcePoints: Record<string, ControlPoint>,
	targetDrawing: Drawing,
	sourcePointId: string,
	targetPointId: string,
): {
	mergedPoints: Record<string, ControlPoint>
	targetIdMap: Record<string, string>
} {
	const mergedPoints: Record<string, ControlPoint> = { ...sourcePoints }
	let counter = Object.keys(mergedPoints).length
	const targetIdMap: Record<string, string> = {}

	for (const [oldId, point] of Object.entries(targetDrawing.points)) {
		if (oldId === targetPointId) {
			// Junction point - map to source point ID (target position is stationary)
			targetIdMap[oldId] = sourcePointId
			// Update the source junction point to target's position (stationary node wins)
			mergedPoints[sourcePointId] = { ...point, id: sourcePointId }
		} else {
			// Non-junction point - create new ID to avoid collision
			const newId = `p-${counter++}`
			targetIdMap[oldId] = newId
			mergedPoints[newId] = { ...point, id: newId }
		}
	}

	return { mergedPoints, targetIdMap }
}

/**
 * Merge segments from source and target, remapping target point IDs.
 */
function mergeSegmentsWithIdMap(
	sourceSegments: PathSegment[],
	targetSegments: PathSegment[],
	targetIdMap: Record<string, string>,
): PathSegment[] {
	return [
		...sourceSegments,
		...targetSegments.map((seg) => ({
			...seg,
			pointIds: seg.pointIds.map((id) => targetIdMap[id] || id),
		})),
	]
}

function reverseSegments(segments: PathSegment[]): PathSegment[] {
	const reversedSegments = [...segments].reverse().map((segment) => ({
		...segment,
		pointIds: [...segment.pointIds].reverse(),
	}))
	return reversedSegments
}

export function mergeDrawings(
	sourceDrawing: Drawing,
	targetDrawing: Drawing,
	sourcePointId: string,
	targetPointId: string,
): Drawing {
	// Determine if we need to reverse segments to make junction points meet
	const sourceIsEnd = isEndPoint(sourceDrawing, sourcePointId)
	const sourceIsStart = isStartPoint(sourceDrawing, sourcePointId)
	const targetIsStart = isStartPoint(targetDrawing, targetPointId)
	const targetIsEnd = isEndPoint(targetDrawing, targetPointId)

	// Compute merged lineEnd - preserve arrows from junction points
	const mergedLineEnd = computeMergedLineEnd(
		sourceDrawing,
		targetDrawing,
		sourceIsEnd,
		targetIsStart,
		targetIsEnd,
	)

	// Get segments in correct order (source ends with sourcePoint, target starts with targetPoint)
	const sourceSegments = sourceIsEnd
		? sourceDrawing.segments
		: sourceIsStart
			? reverseSegments(sourceDrawing.segments)
			: sourceDrawing.segments

	const targetSegments = targetIsStart
		? targetDrawing.segments
		: targetIsEnd
			? reverseSegments(targetDrawing.segments)
			: targetDrawing.segments

	// Merge point pools and remap target point IDs
	const { mergedPoints, targetIdMap } = remapTargetPointIds(
		sourceDrawing.points,
		targetDrawing,
		sourcePointId,
		targetPointId,
	)

	// Merge segments, remapping target point IDs
	const mergedSegments = mergeSegmentsWithIdMap(
		sourceSegments,
		targetSegments,
		targetIdMap,
	)

	return {
		id: crypto.randomUUID(),
		points: mergedPoints,
		segments: mergedSegments,
		style: {
			...sourceDrawing.style,
			lineEnd: mergedLineEnd,
		},
		annotations: [
			...sourceDrawing.annotations,
			...targetDrawing.annotations,
		],
	}
}

/**
 * Delete a point from a drawing, updating segments accordingly.
 * Returns null if the drawing should be deleted entirely (fewer than 2 points remaining).
 */
export function deletePointFromDrawing(
	drawing: Drawing,
	pointIdToDelete: string,
): Drawing | null {
	const pointIds = Object.keys(drawing.points)

	// If deleting would leave fewer than 2 points, return null to signal deletion
	if (pointIds.length <= 2) {
		return null
	}

	// Create new points pool without the deleted point
	const newPoints: Record<string, ControlPoint> = {}
	for (const [id, point] of Object.entries(drawing.points)) {
		if (id !== pointIdToDelete) {
			newPoints[id] = point
		}
	}

	// Get ordered list of remaining point IDs by traversing segments
	const orderedPointIds = getOrderedPointIds(drawing)
	const remainingOrderedPoints = orderedPointIds.filter(id => id !== pointIdToDelete)

	// Rebuild segments as simple lines connecting remaining points in order
	const rebuiltSegments: PathSegment[] = []
	for (let i = 0; i < remainingOrderedPoints.length - 1; i++) {
		rebuiltSegments.push({
			type: 'line',
			pointIds: [remainingOrderedPoints[i]!, remainingOrderedPoints[i + 1]!],
		})
	}

	return {
		...drawing,
		points: newPoints,
		segments: rebuiltSegments,
	}
}

/**
 * Insert a new point into a drawing at the specified segment.
 * Splits the segment into two line segments.
 */
export function insertPointIntoDrawing(
	drawing: Drawing,
	segmentIndex: number,
	newPointPosition: Coordinate,
): Drawing {
	const segment = drawing.segments[segmentIndex]
	if (!segment) return drawing

	// Create new point
	const newPointId = crypto.randomUUID()
	const newPoint: ControlPoint = {
		id: newPointId,
		x: newPointPosition.x,
		y: newPointPosition.y,
		type: 'corner',
	}

	// Add to points pool
	const newPoints = {
		...drawing.points,
		[newPointId]: newPoint,
	}

	// Split the segment into two
	const startPointId = segment.pointIds[0]
	const endPointId = segment.pointIds[segment.pointIds.length - 1]

	if (!startPointId || !endPointId) return drawing

	const newSegment1: PathSegment = {
		type: 'line',
		pointIds: [startPointId, newPointId],
	}

	const newSegment2: PathSegment = {
		type: 'line',
		pointIds: [newPointId, endPointId],
	}

	// Replace the old segment with two new ones
	const newSegments = [
		...drawing.segments.slice(0, segmentIndex),
		newSegment1,
		newSegment2,
		...drawing.segments.slice(segmentIndex + 1),
	]

	return {
		...drawing,
		points: newPoints,
		segments: newSegments,
	}
}

